import { useEffect, useRef } from 'react';
import { X, ChevronRight } from 'lucide-react';
import type { ActiveRecommendation, RecommendationPlacement } from '@parentpath/supabase-client';
import { trackRecommendationEvent } from '@/services/recommendationsService';

export type RecommendationCardVariant = 'extended' | 'compact';

interface Props {
  recommendation: ActiveRecommendation;
  placement: RecommendationPlacement;
  canDismiss: boolean;
  onDismiss: (id: string) => void;
  variant?: RecommendationCardVariant;
}

export function RecommendationCard({ recommendation, placement, canDismiss, onDismiss, variant = 'extended' }: Props) {
  const impressionTracked = useRef(false);
  const cardRef = useRef<HTMLAnchorElement>(null);

  // Track impression once when card becomes visible
  useEffect(() => {
    if (impressionTracked.current || !cardRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !impressionTracked.current) {
            impressionTracked.current = true;
            trackRecommendationEvent(recommendation.id, 'impression', placement);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [recommendation.id, placement]);

  const handleClick = () => {
    trackRecommendationEvent(recommendation.id, 'click', placement);
  };

  const handleDismissClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDismiss(recommendation.id);
  };

  const showImage = recommendation.show_image && recommendation.image_url;
  const ctaLabel = recommendation.cta_label || 'Mehr erfahren';

  if (variant === 'compact') {
    return (
      <a
        ref={cardRef}
        href={recommendation.cta_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="relative block glass-effect rounded-[15px] p-3 hover:bg-white/15 transition-all"
      >
        {canDismiss && (
          <button
            type="button"
            onClick={handleDismissClick}
            className="absolute top-2 right-2 z-10 p-1 rounded-full text-white/40 hover:text-white/80 transition-colors"
            aria-label="Empfehlung ausblenden"
            title="Dauerhaft ausblenden"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Empfehlung label - top left above image + text */}
        <div className="mb-3">
          <span className="inline-block text-xs font-medium px-2 py-0.5 bg-pp-teal border border-white/25 rounded-[5px] text-white">
            {recommendation.label || 'Anzeige'}
          </span>
        </div>

        {/* Image (square, left) + text (right) */}
        <div className="flex gap-3">
          {showImage && (
            <div className="aspect-square w-24 flex-shrink-0 overflow-hidden rounded-[10px]">
              <img
                src={recommendation.image_url!}
                alt=""
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white mb-1 pr-6">{recommendation.title}</h3>
            {recommendation.description && (
              <p className="text-sm text-white/60 line-clamp-3">{recommendation.description}</p>
            )}
          </div>
        </div>

        {/* Full-width CTA bar below image + text */}
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
          <span className="text-sm text-white/80">{ctaLabel}</span>
          <ChevronRight className="w-4 h-4 text-pp-orange" />
        </div>
      </a>
    );
  }

  // extended variant (default) — used by Wissenswertes sections
  return (
    <a
      ref={cardRef}
      href={recommendation.cta_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="relative block glass-effect rounded-[15px] overflow-hidden hover:bg-white/15 transition-all"
    >
      {canDismiss && (
        <button
          type="button"
          onClick={handleDismissClick}
          className="absolute top-2 right-2 z-10 p-1 rounded-full text-white/40 hover:text-white/80 transition-colors"
          aria-label="Empfehlung ausblenden"
          title="Dauerhaft ausblenden"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {showImage && (
        <div className="aspect-video w-full overflow-hidden">
          <img
            src={recommendation.image_url!}
            alt=""
            className="w-full h-full object-cover object-center"
            loading="lazy"
          />
        </div>
      )}

      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="inline-block text-xs font-medium px-2 py-0.5 bg-pp-teal border border-white/25 rounded-[5px] text-white">
            {recommendation.label || 'Anzeige'}
          </span>
        </div>
        <h2 className="font-semibold text-white mb-1 pr-6">{recommendation.title}</h2>
        {recommendation.description && (
          <p className="text-sm text-white/60 line-clamp-2">{recommendation.description}</p>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-white/80">{ctaLabel}</span>
          <ChevronRight className="w-4 h-4 text-pp-orange" />
        </div>
      </div>
    </a>
  );
}
